import { useEffect, useState, useRef } from "react";
import { Image, Plus, Pencil, Trash2, Eye, EyeOff, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getAllGallery, createGalleryImage, updateGalleryImage, deleteGalleryImage, uploadFile } from "@/lib/api";

const categoryOptions = [
  { value: "algemeen", label: "Algemeen" },
  { value: "activiteiten", label: "Activiteiten" },
  { value: "evenementen", label: "Evenementen" }
];

const emptyForm = {
  title: "",
  description: "",
  image_url: "",
  category: "algemeen",
  order: 0,
  is_active: true
};

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchImages = async () => {
    try {
      const res = await getAllGallery();
      setImages(res.data);
    } catch (error) {
      toast.error("Kon afbeeldingen niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (image) => {
    setFormData({
      title: image.title,
      description: image.description || "",
      image_url: image.image_url,
      category: image.category,
      order: image.order,
      is_active: image.is_active
    });
    setSelectedImage(image);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Alleen afbeeldingen zijn toegestaan");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFile(file);
      setFormData({...formData, image_url: res.data.url});
      toast.success("Afbeelding geüpload");
    } catch (error) {
      toast.error("Kon afbeelding niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.image_url) {
      toast.error("Titel en afbeelding zijn verplicht");
      return;
    }

    try {
      if (isEditing) {
        await updateGalleryImage(selectedImage.id, formData);
        toast.success("Afbeelding bijgewerkt");
      } else {
        await createGalleryImage(formData);
        toast.success("Afbeelding toegevoegd");
      }
      setDialogOpen(false);
      fetchImages();
    } catch (error) {
      toast.error("Kon afbeelding niet opslaan");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGalleryImage(selectedImage.id);
      toast.success("Afbeelding verwijderd");
      setDeleteDialogOpen(false);
      fetchImages();
    } catch (error) {
      toast.error("Kon afbeelding niet verwijderen");
    }
  };

  const toggleActive = async (image) => {
    try {
      await updateGalleryImage(image.id, { is_active: !image.is_active });
      toast.success(image.is_active ? "Afbeelding verborgen" : "Afbeelding zichtbaar");
      fetchImages();
    } catch (error) {
      toast.error("Kon status niet wijzigen");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="aspect-square bg-slate-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-gallery-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Image className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Galerij</h1>
            <p className="text-slate-600">Sport Eldorado foto's beheren</p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Afbeelding Toevoegen
        </Button>
      </div>

      {/* Gallery Grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {images.map((image) => (
            <Card key={image.id} className={`overflow-hidden ${!image.is_active ? 'opacity-50' : ''}`}>
              <div className="aspect-square relative">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <Badge className={image.is_active ? "bg-green-500" : "bg-slate-500"}>
                    {image.is_active ? "Actief" : "Verborgen"}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 truncate">{image.title}</h3>
                <p className="text-sm text-slate-500">{categoryOptions.find(c => c.value === image.category)?.label}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(image)}>
                    {image.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleOpenEdit(image)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => { setSelectedImage(image); setDeleteDialogOpen(true); }}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-16">
          <CardContent>
            <Image className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-bold mb-2 text-slate-900">Geen afbeeldingen</h3>
            <p className="text-slate-500">Voeg je eerste afbeelding toe aan de galerij.</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{isEditing ? "Afbeelding Bewerken" : "Afbeelding Toevoegen"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Titel van de afbeelding"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Beschrijving</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionele beschrijving"
                rows={2}
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Afbeelding *</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="URL of upload een afbeelding"
                  className="flex-1 border-slate-300"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-slate-300"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "..." : "Upload"}
                </Button>
              </div>
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg mt-2" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Categorie</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Volgorde</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  className="border-slate-300"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label className="text-slate-700">Actief (zichtbaar op website)</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">Annuleren</Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isEditing ? "Opslaan" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Afbeelding Verwijderen</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">Weet je zeker dat je deze afbeelding wilt verwijderen?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">Annuleren</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
